<template>
    <div
        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-transition-[opacity,height] tw-duration-500 tw-ease-in-out"
        :class="{
            '!tw-h-0 !tw-opacity-0': hideTooltip,
            'tw-h-6 tw-opacity-100': !hideTooltip,
        }">
        <span
            v-if="!isOnline"
            class="tw-text-gray-500 tw-text-sm tw-font-medium">
            {{ $t('PostsLoader.offline') }}
        </span>
        <template v-else-if="isOnline && !hasError">
            <v-progress-circular
                v-if="!hasFinished"
                color="primary"
                indeterminate
                size="22"
                width="2">
            </v-progress-circular>
            <span
                v-else-if="hasFinished"
                class="tw-text-gray-500 tw-text-sm tw-font-medium">
                {{ $t('PostsLoader.updated') }}
            </span>
        </template>
        <span
            v-if="hasError"
            class="tw-text-sm tw-font-medium tw-text-red">
            {{ $t('PostsLoader.error') }}
        </span>
    </div>
    <slot></slot>
</template>

<script setup lang="ts">
    import { fetchAds, fetchOpinions, fetchPosts } from '@/api/posts';
    import { useIndexedDBStore } from '@/composables/useIndexDB';
    import { downloadImages, downloadImagesFromOpinions } from '@/composables/usePost';
    import categories from '@/configs/categories';
    import type { IOpinion, WPPagination, WPPost } from '@/contentTypes';
    import _ from 'lodash';
    import { useAppStore } from '@/store/app';
    import { POSTS_PER_PAGE } from '@/configs/posts';
    import { useOnline } from '@vueuse/core';
    import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue';

    const appStore = useAppStore();
    const isOnline = useOnline();

    const isLoading = ref(false);
    const hideTooltip = ref(true);
    const hasError = ref(false);
    const lastFocusTime = ref(0);

    const query = {
        page: 1,
        per_page: POSTS_PER_PAGE,
        categories: '',
    };

    const hasFinished = computed(() => {
        return !isLoading.value;
    });

    async function saveAds() {
        const { data } = await fetchAds();
        if(!data || !Array.isArray(data) || data.length === 0) {
            appStore.setAds([]);
        } else {
            appStore.setAds(data);
        }
    }

    async function init() {
        if (!isOnline.value) {
            return;
        }

        // Prevent multiple concurrent init calls
        if (isLoading.value) {
            return;
        }

        const allPosts: WPPost[] = [];
        const allOpinions: IOpinion[] = [];
        isLoading.value = true;
        hideTooltip.value = false;
        hasError.value = false;
        try {
            const indexDB = useIndexedDBStore('posts');
            const indexDBOpinions = useIndexedDBStore('opinions');

            const currentPosts = await indexDB.getAll();
            const currentOpinions = await indexDBOpinions.getAll();
            const currentDate = Date.now();

            async function _fetchPosts(categoryIds: number[] = []) {
                const _query = _.cloneDeep(query);

                _query.categories = categoryIds.join(',');

                const { data } = await fetchPosts(_query);

                const dataKeys = Object.keys(data);
                if (!dataKeys.length) {
                    return;
                }

                const _posts: WPPost[] = [];

                for (const key of dataKeys) {
                    const { posts, pagination } = data[key];
                    if (pagination && _.has(pagination, 'total')) {
                        appStore.setPostsTotalByType(key, pagination.total);
                    }
                    if (!posts || !Array.isArray(posts) || posts.length === 0) {
                        continue;
                    }

                    _posts.push(...posts);
                }

                // sort posts by date
                _posts.sort((a: WPPost, b: WPPost) => new Date(b.date).getTime() - new Date(a.date).getTime());

                // Use Set for O(1) lookup instead of Array.some() which is O(n)
                const newPostIds = new Set(_posts.map((p) => p.id));

                // Filter current posts for this category
                const _currentPosts = currentPosts.filter((post: WPPost) => {
                    const ids = post.categoryWpIds ?? post.categories ?? [];
                    return ids.some((id) => categoryIds.includes(id));
                });

                // Find posts to delete (exist in cache but not in new data)
                const toDeleteIds = _currentPosts.filter((post: WPPost) => !newPostIds.has(post.id)).map((post) => post.id);

                // Batch delete in a single transaction
                if (toDeleteIds.length > 0) {
                    await indexDB.deleteMany(toDeleteIds);
                }

                // Batch save all posts in a single transaction
                if (_posts.length > 0) {
                    await indexDB.set(_posts);
                }

                // Avoid unnecessary deep cloning - use spread for shallow copy if needed
                allPosts.push(..._posts);
            }
            async function _fetchOpinions() {
                const { data } = await fetchOpinions({
                    per_page: POSTS_PER_PAGE,
                });
                const { posts } = data as { posts: IOpinion[]; pagination: WPPagination };

                if (!posts || !Array.isArray(posts) || posts.length === 0) {
                    appStore.setPostsTotalByType('opinions', 0);
                    for (const post of currentOpinions) {
                        await indexDB.delete(post.id);
                    }
                    return;
                }

                appStore.setPostsTotalByType('opinions', data.pagination?.total || 0);

                // Use Set for O(1) lookup instead of Array.some() which is O(n)
                const newOpinionIds = new Set(posts.map((p) => p.id));
                const toDeleteIds = currentOpinions.filter((post: IOpinion) => !newOpinionIds.has(post.id)).map((post) => post.id);

                // Batch delete in a single transaction
                if (toDeleteIds.length > 0) {
                    await indexDBOpinions.deleteMany(toDeleteIds);
                }

                // Batch save all opinions in a single transaction
                if (posts.length > 0) {
                    await indexDBOpinions.set(posts);
                }

                // Avoid unnecessary deep cloning
                allOpinions.push(...posts);
            }
            await _fetchPosts(categories.map((category) => category.wordpressId));
            await _fetchOpinions();

            // Ensure we only keep the most recent posts for offline use
            // This prevents accumulation of old posts over time
            // Calculate max based on categories * posts per page
            // Note: Posts can belong to multiple categories, but IndexedDB deduplicates by ID
            // So actual stored posts will be <= this number
            const maxPosts = categories.filter((c) => c.showInNews).length * POSTS_PER_PAGE;
            await indexDB.keepMostRecent(maxPosts, 'date');

            // Keep most recent opinions (same as posts per page)
            const maxOpinions = POSTS_PER_PAGE;
            await indexDBOpinions.keepMostRecent(maxOpinions, 'date');

            appStore.setPostsLastFetched(currentDate);

            // Reset so the next focus-after-10-min can refetch again (we refetch in-place, no reload)
            appStore.setAlreadyRefreshed(false);
            downloadImages(allPosts);
            downloadImagesFromOpinions(allOpinions);

            hideTooltipTimeout = setTimeout(() => {
                hideTooltip.value = true;
            }, 2000);
        } catch (error) {
            console.error('Error loading posts:', error);
            hasError.value = true;
        } finally {
            isLoading.value = false;

            initTimeout = setTimeout(() => {
                init();
            }, 300000);
        }
    }

    init();
    saveAds();

    const stopOnlineWatcher = watch(isOnline, (newVal) => {
        if (newVal) {
            init();
            saveAds();
        }
    });

    const hasLoadedPosts = computed(() => {
        return hasFinished.value || hasError.value;
    });

    // Store the focus event handler reference for cleanup
    let focusHandler: (() => Promise<void>) | null = null;
    let initTimeout: ReturnType<typeof setTimeout> | null = null;
    let hideTooltipTimeout: ReturnType<typeof setTimeout> | null = null;

    onMounted(() => {
        // Check if we're in an in-app browser to avoid infinite refresh loops
        const isInAppBrowser = /FBAN|FBAV|Instagram|WhatsApp|Line|Telegram|Twitter|LinkedInApp|Snapchat|Pinterest|TikTok|WeChat|QQ|UCBrowser|Opera Mini|SamsungBrowser|MiuiBrowser|HuaweiBrowser/i.test(
            navigator.userAgent,
        );

        if (!isInAppBrowser) {
            focusHandler = async () => {
                const currentTime = Date.now();

                // Debounce: ignore focus events within 500ms of each other
                if (currentTime - lastFocusTime.value < 500) {
                    return;
                }
                lastFocusTime.value = currentTime;

                const timeSinceLastFetch = currentTime - appStore.getPostsLastFetched;
                const hasBeenLongEnough = timeSinceLastFetch > 10 * 60 * 1000; // 10 minutes
                const notRecentlyRefreshed = !appStore.getAlreadyRefreshed;

                if (hasBeenLongEnough && notRecentlyRefreshed) {
                    appStore.setAlreadyRefreshed(true);
                    await init();
                }
            };
            window.addEventListener('focus', focusHandler);
        }
    });

    onUnmounted(() => {
        stopOnlineWatcher();
        // Clean up the focus event listener
        if (focusHandler) {
            window.removeEventListener('focus', focusHandler);
        }

        // Clean up init timeout
        if (initTimeout) {
            try {
                clearTimeout(initTimeout);
            } catch (error) {
                console.warn('Error clearing init timeout:', error);
            }
        }
        // Clean up hide tooltip timeout
        if (hideTooltipTimeout) {
            try {
                clearTimeout(hideTooltipTimeout);
            } catch (error) {
                console.warn('Error clearing hide tooltip timeout:', error);
            }
        }
    });

    provide('hasLoadedPosts', hasLoadedPosts);
</script>

<style scoped></style>
